import React, { useState, useEffect } from 'react';
import { 
  SettingOutlined, 
  DownOutlined, 
  EyeOutlined,
  AppstoreOutlined,
  UserOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
  AimOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  DollarOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { Select, InputNumber, DatePicker, Switch } from 'antd';
import { message } from 'antd';
import { useAuth } from '../../../../../contexts/AuthContext';

const performanceGoals = [
  {
    id: 'REACH',
    icon: <EyeOutlined />,
    title: 'Maximize reach of ads',
    description: 'Show your ads to the maximum number of people',
  },
  {
    id: 'IMPRESSIONS',
    icon: <AppstoreOutlined />,
    title: 'Maximize number of impressions',
    description: 'Show your ads as many times as possible',
  },
  {
    id: 'AD_RECALL',
    icon: <UserOutlined />,
    title: 'Maximize ad recall lift',
    description: 'Show ads to people most likely to remember them',
  },
  {
    id: 'THRUPLAY',
    icon: <PlayCircleOutlined />,
    title: 'Maximize ThruPlay views',
    description: 'Get more complete video views (15+ seconds)',
  },
  {
    id: 'VIDEO_VIEWS',
    icon: <VideoCameraOutlined />,
    title: 'Maximize 2-second continuous video views',
    description: 'Get people to watch at least 2 seconds of your video',
  },
];

const frequencyControlOptions = [
  {
    id: 'TARGET',
    icon: <AimOutlined />,
    title: 'Target',
    description: 'Aim for an average frequency',
  },
  {
    id: 'CAP',
    icon: <ClockCircleOutlined />,
    title: 'Cap',
    description: 'Set a maximum frequency limit',
  },
];

const MetaAdSetSettings = ({ campaignData, onUpdate, onNext }) => {
  const { token } = useAuth();
  const [adSetName, setAdSetName] = useState(campaignData.metaAds?.adSetName || '');
  const [selectedGoal, setSelectedGoal] = useState(campaignData.metaAds?.performanceGoal || 'REACH');
  const [facebookPages, setFacebookPages] = useState(campaignData.metaAds?.facebookPages || []);
  const [instagramAccounts, setInstagramAccounts] = useState(campaignData.metaAds?.instagramAccounts || []);
  const [frequencyControl, setFrequencyControl] = useState(campaignData.metaAds?.frequencyControl || 'TARGET');
  const [targetFrequency] = useState(campaignData.metaAds?.targetFrequency || 2);
  const [frequencyTimes, setFrequencyTimes] = useState(campaignData.metaAds?.frequencyTimes || 2);
  const [frequencyPeriod, setFrequencyPeriod] = useState(campaignData.metaAds?.frequencyPeriod || 7);
  const [frequencyUnit, setFrequencyUnit] = useState(campaignData.metaAds?.frequencyUnit || 'day(s)');
  
  // Budget & Bid Strategy fields (only shown when advantagePlusBudget is enabled)
  const [budgetType, setBudgetType] = useState(campaignData.metaAds?.budgetType || 'DAILY');
  const [budgetAmount, setBudgetAmount] = useState(campaignData.metaAds?.budgetAmount || '');
  const [currency, setCurrency] = useState(campaignData.metaAds?.currency || 'USD');
  const [bidStrategy, setBidStrategy] = useState(campaignData.metaAds?.bidStrategy || 'HIGHEST_VOLUME');
  const [startDate, setStartDate] = useState(campaignData.metaAds?.startDate ? new Date(campaignData.metaAds.startDate) : null);
  const [endDate, setEndDate] = useState(campaignData.metaAds?.endDate ? new Date(campaignData.metaAds.endDate) : null);
  const [budgetSchedulingEnabled, setBudgetSchedulingEnabled] = useState(campaignData.metaAds?.budgetSchedulingEnabled || false);
  
  // Attribution Setting
  const [attributionSetting, setAttributionSetting] = useState(campaignData.metaAds?.attributionSetting || '7_DAY_CLICK'); // eslint-disable-line no-unused-vars
  
  // State for loaded accounts from API
  const [facebookPageOptions, setFacebookPageOptions] = useState([]);
  const [instagramAccountOptions, setInstagramAccountOptions] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  // Show inline field errors only after the user tries to continue
  const [continueAttempted, setContinueAttempted] = useState(false);
  const adSetNameError = continueAttempted && !adSetName.trim();
  const facebookPagesError = continueAttempted && facebookPages.length === 0;

  useEffect(() => {
    const fetchConnectedAccounts = async () => {
      if (!token) {
        setLoadingAccounts(false);
        return;
      }
      
      const mockFacebookPages = [
        { value: 'mock_fb_page_1', label: 'Justin Bailey' },
        { value: 'mock_fb_page_2', label: 'Homelytics' }
      ];
      const mockInstagramAccounts = [
        { value: 'mock_ig_1', label: '@justinbailey' },
        { value: 'mock_ig_2', label: '@homelytics' }
      ];

      try {
        const fbRes = await fetch('/api/integrations/facebook/pages', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (fbRes.ok) {
          const fbData = await fbRes.json();
          const pages = fbData.pages || fbData || [];
          setFacebookPageOptions(pages.length > 0
            ? pages.map(page => ({
                value: page.id,
                label: page.account_name ? `${page.name || page.id} — ${page.account_name}` : (page.name || page.id),
                account_slot: page.account_slot,
              }))
            : mockFacebookPages
          );
        } else {
          setFacebookPageOptions(mockFacebookPages);
        }
        
        const igRes = await fetch('/api/integrations/facebook/instagram-accounts', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (igRes.ok) {
          const igData = await igRes.json();
          const accounts = igData.accounts || igData || [];
          setInstagramAccountOptions(accounts.length > 0
            ? accounts.map(account => ({ value: account.id, label: account.username || account.name || account.id }))
            : mockInstagramAccounts
          );
        } else {
          setInstagramAccountOptions(mockInstagramAccounts);
        }
      } catch (error) {
        console.error('Failed to fetch connected accounts:', error);
        setFacebookPageOptions(mockFacebookPages);
        setInstagramAccountOptions(mockInstagramAccounts);
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchConnectedAccounts();
  }, [token]);

  // Keep parent data in sync as fields change (single-page campaign view)
  useEffect(() => {
    const selectedPageSlots = facebookPages
      .map((pageId) => facebookPageOptions.find((opt) => opt.value === pageId)?.account_slot)
      .filter((slot) => slot != null);
    onUpdate({
      metaAds: {
        adSetName,
        performanceGoal: selectedGoal,
        facebookPages,
        accountSlot: selectedPageSlots[0] ?? null,
        instagramAccounts,
        frequencyControl,
        targetFrequency,
        attributionSetting,
        ...(campaignData.metaAds?.advantagePlusBudget && {
          budgetType,
          budgetAmount,
          currency,
          bidStrategy,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          budgetSchedulingEnabled,
        }),
      }
    });
  }, [adSetName, selectedGoal, facebookPages, facebookPageOptions, instagramAccounts, frequencyControl, targetFrequency, attributionSetting, budgetType, budgetAmount, currency, bidStrategy, startDate, endDate, budgetSchedulingEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    setContinueAttempted(true);
    if (!adSetName.trim()) {
      message.warning('Please enter an ad set name');
      return;
    }
    if (facebookPages.length === 0) {
      message.warning('Please select at least one Facebook Page');
      return;
    }
    // The selected Page determines which connected Facebook account (and ad account)
    // the campaign gets created on — without this, the backend defaults to the
    // first-connected account, which silently posts to the wrong ad account.
    const selectedPageSlots = facebookPages
      .map((pageId) => facebookPageOptions.find((opt) => opt.value === pageId)?.account_slot)
      .filter((slot) => slot != null);
    if (new Set(selectedPageSlots).size > 1) {
      message.warning('Selected pages belong to different connected Facebook accounts. Please select pages from only one account.');
      return;
    }
    const accountSlot = selectedPageSlots[0] ?? null;
    onUpdate({
      metaAds: {
        ...campaignData.metaAds,
        performanceGoal: selectedGoal,
        facebookPages,
        accountSlot,
        instagramAccounts,
        frequencyControl,
        targetFrequency,
        attributionSetting,
        ...(campaignData.metaAds?.advantagePlusBudget && {
          budgetType,
          budgetAmount,
          currency,
          bidStrategy,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          budgetSchedulingEnabled,
        }),
      }
    });
    onNext();
  };

  return (
    <div className="step-container" style={{ maxWidth: '960px', margin: '0 auto', padding: '0 20px 100px' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px', background: '#f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px',
        }}>
          <SettingOutlined style={{ fontSize: '32px', color: '#111827' }} />
        </div>
        <h1 style={{ fontSize: '32px', fontWeight: 700, color: '#000', marginBottom: '12px', textAlign: 'center' }}>
          Ad Set Settings
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--ink-200)', marginBottom: 0, textAlign: 'center' }}>
          Configure your ad set name, performance goals, and settings
        </p>
      </div>

      {/* ── Row 1: Ad Set Name · Facebook Page · Instagram Account (3 columns) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '32px' }}>
        {/* Ad Set Name */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Ad Set Name <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Enter ad set name..."
            value={adSetName}
            onChange={(e) => setAdSetName(e.target.value)}
            style={{
              width: '100%', padding: '0 16px', fontSize: '14px', height: '40px',
              border: `1px solid ${adSetNameError ? '#ef4444' : 'var(--paper-300)'}`, borderRadius: '8px', outline: 'none',
              boxSizing: 'border-box', transition: 'border-color 0.2s ease',
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--ink)'}
            onBlur={(e) => e.target.style.borderColor = adSetNameError ? '#ef4444' : 'var(--paper-300)'}
          />
          {adSetNameError && (
            <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>Ad set name is required</div>
          )}
        </div>

        {/* Facebook Pages */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Facebook Page(s) <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Select
            mode="multiple"
            status={facebookPagesError ? 'error' : undefined}
            value={facebookPages}
            onChange={setFacebookPages}
            placeholder={loadingAccounts ? 'Loading pages...' : 'Select Facebook Page(s)'}
            style={{ width: '100%' }}
            loading={loadingAccounts}
            notFoundContent={loadingAccounts ? <LoadingOutlined spin /> : 'No pages found. Connect Facebook in Settings.'}
            options={facebookPageOptions}
          />
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '4px' }}>
            {facebookPageOptions.length === 0 && !loadingAccounts
              ? <span style={{ color: '#ef4444' }}>No pages connected. Go to Settings → Integrations.</span>
              : facebookPagesError
                ? <span style={{ color: '#ef4444' }}>Please select at least one Facebook Page</span>
                : 'Select one or more pages'}
          </div>
        </div>

        {/* Instagram Accounts */}
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
            Instagram Account(s)
          </label>
          <Select
            mode="multiple"
            value={instagramAccounts}
            onChange={setInstagramAccounts}
            placeholder={loadingAccounts ? 'Loading accounts...' : 'Select Instagram account(s)'}
            style={{ width: '100%' }}
            loading={loadingAccounts}
            notFoundContent={loadingAccounts ? <LoadingOutlined spin /> : 'No accounts found'}
            options={instagramAccountOptions}
          />
          <div style={{ fontSize: '12px', color: 'var(--ink-200)', marginTop: '4px' }}>
            Select one or more accounts (optional)
          </div>
        </div>
      </div>

      {/* ── Performance Goal ── */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Performance Goal
        </label>
        <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginBottom: '16px' }}>
          Choose how you want to optimize ad delivery
        </div>
        
        {/* 5 cards across one row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
          {performanceGoals.map((goal) => (
            <div
              key={goal.id}
              onClick={() => setSelectedGoal(goal.id)}
              style={{
                padding: '16px',
                background: selectedGoal === goal.id ? '#eff6ff' : '#fff',
                border: selectedGoal === goal.id ? '2px solid var(--ink)' : '1px solid var(--paper-300)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => { if (selectedGoal !== goal.id) e.currentTarget.style.borderColor = 'var(--paper-300)'; }}
              onMouseLeave={(e) => { if (selectedGoal !== goal.id) e.currentTarget.style.borderColor = 'var(--paper-300)'; }}
            >
              <div style={{
                width: '36px', height: '36px', borderRadius: '9px',
                background: selectedGoal === goal.id ? 'var(--ink)' : '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '10px', fontSize: '18px',
                color: selectedGoal === goal.id ? '#fff' : 'var(--ink-200)',
              }}>
                {goal.icon}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px', lineHeight: 1.3 }}>
                {goal.title}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--ink-200)', lineHeight: 1.4 }}>
                {goal.description}
              </div>
              {selectedGoal === goal.id && (
                <div style={{
                  position: 'absolute', top: '10px', right: '10px',
                  width: '18px', height: '18px', borderRadius: '50%',
                  background: 'var(--ink)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: '#fff', fontSize: '11px',
                }}>✓</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Frequency Control ── */}
      <div style={{ marginBottom: '32px' }}>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Frequency Control
        </label>
        <div style={{ fontSize: '13px', color: 'var(--ink-200)', marginBottom: '16px' }}>
          Control how often people see your ads
          <span style={{
            display: 'inline-block', marginLeft: '8px', padding: '2px 8px',
            background: '#dbeafe', color: '#1e40af', borderRadius: '4px',
            fontSize: '11px', fontWeight: 600
          }}>
            ℹ Important for awareness campaigns
          </span>
        </div>

        {/* ── Row: Frequency Type (2 cards) + inline settings (1 block) → 3-col grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '12px', alignItems: 'start' }}>
          {frequencyControlOptions.map((option) => (
            <div
              key={option.id}
              onClick={() => setFrequencyControl(option.id)}
              style={{
                padding: '16px',
                background: frequencyControl === option.id ? '#eff6ff' : '#fff',
                border: frequencyControl === option.id ? '2px solid var(--ink)' : '1px solid var(--paper-300)',
                borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s ease',
                display: 'flex', alignItems: 'center', gap: '12px',
              }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                background: frequencyControl === option.id ? 'var(--ink)' : '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', color: frequencyControl === option.id ? '#fff' : 'var(--ink-200)',
              }}>
                {option.icon}
              </div>
              <div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827', marginBottom: '2px' }}>
                  {option.title}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--ink-200)' }}>{option.description}</div>
              </div>
            </div>
          ))}

          {/* Inline frequency settings */}
          <div style={{
            background: '#f9fafb', border: '1px solid var(--paper-300)',
            borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap', fontSize: '14px', color: '#111827' }}>
              <span>Show ads</span>
              <InputNumber min={1} max={10} value={frequencyTimes} onChange={setFrequencyTimes} style={{ width: '72px' }} />
              <span>time(s) every</span>
              <InputNumber min={1} max={90} value={frequencyPeriod} onChange={setFrequencyPeriod} style={{ width: '72px' }} />
              <Select
                value={frequencyUnit}
                onChange={setFrequencyUnit}
                style={{ width: '110px' }}
                options={[
                  { value: 'day(s)', label: 'day(s)' },
                  { value: 'week(s)', label: 'week(s)' },
                ]}
              />
            </div>
            <div style={{ fontSize: '12px', color: 'var(--ink)', marginTop: '10px', lineHeight: 1.5 }}>
              We'll try to show your ads to each person an average of {frequencyTimes} time(s) every {frequencyPeriod} {frequencyUnit}.
            </div>
          </div>
        </div>
      </div>

      {/* ── Budget & Bid Strategy (only when advantagePlusBudget is enabled) ── */}
      {campaignData.metaAds?.advantagePlusBudget && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{ background: '#fff', border: '1px solid var(--paper-300)', borderRadius: '12px', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '12px', background: '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px',
              }}>
                <DollarOutlined style={{ fontSize: '24px', color: 'var(--ink-200)' }} />
              </div>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                  Budget & Bid Strategy
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--ink-200)', margin: 0 }}>
                  Configure your campaign-level budget and bidding strategy
                </p>
              </div>
            </div>

            {/* ── Row 1: Budget Type · Budget Amount · Bid Strategy (3 columns) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              {/* Budget Type */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Budget Type
                </label>
                <Select
                  value={budgetType}
                  onChange={setBudgetType}
                  style={{ width: '100%' }}
                  size="large"
                  options={[
                    { value: 'DAILY', label: 'Daily Budget' },
                    { value: 'LIFETIME', label: 'Lifetime Budget' },
                  ]}
                />
              </div>

              {/* Budget Amount & Currency */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Budget Amount
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <InputNumber
                    value={budgetAmount}
                    onChange={setBudgetAmount}
                    placeholder="0.00"
                    min={0}
                    precision={2}
                    style={{ flex: 1 }}
                    size="large"
                    keyboard
                    addonBefore="$"
                    parser={(val) => val?.replace(/[^0-9.]/g, '')}
                    formatter={(val) => val ? `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                  />
                  <Select
                    value={currency}
                    onChange={setCurrency}
                    style={{ width: '80px' }}
                    size="large"
                    options={[
                      { value: 'USD', label: 'USD' },
                      { value: 'EUR', label: 'EUR' },
                      { value: 'GBP', label: 'GBP' },
                      { value: 'CAD', label: 'CAD' },
                    ]}
                  />
                </div>
              </div>

              {/* Bid Strategy */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Campaign Bid Strategy
                </label>
                <Select
                  value={bidStrategy}
                  onChange={setBidStrategy}
                  style={{ width: '100%' }}
                  size="large"
                  options={[
                    { value: 'HIGHEST_VOLUME', label: 'Highest Volume' },
                    { value: 'COST_PER_RESULT_GOAL', label: 'Cost Per Result Goal' },
                    { value: 'BID_CAP', label: 'Bid Cap' },
                    { value: 'ROAS_GOAL', label: 'ROAS Goal' },
                  ]}
                />
              </div>
            </div>

            {/* ── Row 2: Start Date · End Date · Budget Scheduling toggle (3 columns) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', alignItems: 'start' }}>
              {/* Start Date */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  Start Date & Time
                </label>
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Select start date & time"
                  style={{ width: '100%' }}
                  size="large"
                />
              </div>

              {/* End Date */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
                  End Date & Time
                  {budgetType === 'DAILY' && <span style={{ fontSize: '12px', color: 'var(--ink-200)', fontWeight: 'normal' }}> (Optional)</span>}
                  {budgetType === 'LIFETIME' && <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: 'normal' }}> (Required)</span>}
                </label>
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  placeholder={budgetType === 'LIFETIME' ? 'Select end date & time' : 'Optional for daily budget'}
                  style={{ width: '100%' }}
                  size="large"
                />
                {budgetType === 'LIFETIME' && !endDate && (
                  <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                    End date is required for lifetime budgets
                  </div>
                )}
              </div>

              {/* Budget Scheduling toggle — inline in same row */}
              <div style={{
                background: '#f9fafb', border: '1px solid var(--paper-300)', borderRadius: '10px',
                padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginTop: '24px', // align with inputs (label height offset)
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '8px', background: '#fff',
                    border: '1px solid var(--paper-300)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CalendarOutlined style={{ fontSize: '18px', color: 'var(--ink-200)' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '2px' }}>
                      Budget Scheduling
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-200)' }}>
                      Set time slots with increased budget
                    </div>
                  </div>
                </div>
                <Switch
                  checked={budgetSchedulingEnabled}
                  onChange={setBudgetSchedulingEnabled}
                  style={{ background: budgetSchedulingEnabled ? 'var(--ink)' : 'var(--paper-300)', flexShrink: 0 }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: '#fff', borderTop: '1px solid var(--paper-300)',
        padding: '16px 24px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center', zIndex: 10
      }}>
        <div style={{ fontSize: '14px', color: 'var(--ink-200)' }}>
          Building: {adSetName || 'Untitled Ad Set'}
        </div>
        <button
          onClick={handleContinue}
          style={{
            padding: '12px 32px',
            background: adSetName.trim() ? 'var(--ink-200)' : 'var(--paper-300)',
            color: '#fff', border: 'none', borderRadius: '8px',
            fontSize: '15px', fontWeight: 600,
            cursor: adSetName.trim() ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', gap: '8px',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => { if (adSetName.trim()) e.currentTarget.style.background = '#4b5563'; }}
          onMouseLeave={(e) => { if (adSetName.trim()) e.currentTarget.style.background = 'var(--ink-200)'; }}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default MetaAdSetSettings;